import { ReactNode } from 'react';
import { isNil, noop } from 'lodash-es';
import { Chip } from '@ds';
import { ConditionalRender, UploadedFile } from '@ui';
import { DonorData, DonorType, OrganType } from '@/api/apis/case';
import { TMDXFile } from '@/api/apis/files';
import { CAUSE_OF_DEATH_LABELS, GENDER_LABELS, NEURO_STATUS_LABELS } from '@/constants/donor-data.constants';
import { calcBmi, cmToIn, gramsToKg, inToFtIn, kgToLb, mmToCm, yesNo } from '@/utils/helpers';
import { DataLink } from './DataLink';
import { DataText } from './DataText';

const gramsToKgFormatted = (grams: number) => `${gramsToKg(grams, 3)} kg`;

const gramsToLbFormatted = (grams: number) => `${kgToLb(gramsToKg(grams), 2)} lb`;

const mmToCmFormatted = (mm: number) => `${mmToCm(mm, 2)} cm`;

const mmToFtInFormatted = (mm: number) => {
  const ftIn = inToFtIn(cmToIn(mmToCm(mm)), 2);
  return `${ftIn.ft} ft  ${ftIn.in} in`;
};

type DataRowProps = { label: string; children: ReactNode };

const DataRow = ({ label, children }: DataRowProps) => (
  <div className="flex min-w-0 items-start min-h-10 px-3 py-2">
    <span className="w-50 shrink-0 text-m-r text-base-20">{label}</span>
    <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">{children}</div>
  </div>
);

type DataSectionProps = { title: string; children: ReactNode };

const DataSection = ({ title, children }: DataSectionProps) => (
  <div className="flex flex-col gap-2">
    <span className="px-1 text-s-m text-base-25">{title}</span>
    <div className="bg-white rounded-lg">{children}</div>
  </div>
);

type DataFieldProps<T> = {
  value: T | null | undefined;
  children: (value: T) => ReactNode;
};

const DataField = <T,>({ value, children }: DataFieldProps<T>) => (
  <ConditionalRender show={!isNil(value)} placeholder="-">
    {!isNil(value) ? children(value) : null}
  </ConditionalRender>
);

type OpoDonorDataViewProps = {
  matchId?: string | null;
  donorData?: DonorData | null;
  organType?: OrganType | null;
  donorType?: DonorType | null;
};

const CtScanDataRows = ({
  ctScan,
  ctScanDetails,
  ctScanLink,
  ctScanDocuments,
}: {
  ctScan?: boolean | null;
  ctScanDetails?: string | null;
  ctScanLink?: string | null;
  ctScanDocuments?: TMDXFile[] | null;
}) => (
  <>
    <DataRow label="CT Scan">
      <DataField value={ctScan}>{(v) => <Chip size="sm">{yesNo(v)}</Chip>}</DataField>
    </DataRow>

    {ctScan && (
      <>
        <DataRow label="CT Scan Details">
          <DataField value={ctScanDetails}>{(v) => <DataText>{v}</DataText>}</DataField>
        </DataRow>

        <DataRow label="CT Scan Link">
          <DataField value={ctScanLink || null}>{(url) => <DataLink url={url} />}</DataField>
        </DataRow>

        <DataRow label="CT Scan Documents">
          <DataField value={ctScanDocuments?.length ? ctScanDocuments : null}>
            {(files) => (
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {files!.map((file) => (
                  <UploadedFile key={file.id} file={file} showDeleteBtn={false} showDownloadBtn onDelete={noop} />
                ))}
              </div>
            )}
          </DataField>
        </DataRow>
      </>
    )}
  </>
);

const LiverAdditionalInfo = ({
  liverData,
  reasonForUnallocated,
}: {
  liverData: DonorData['liverData'];
  reasonForUnallocated?: string | null;
}) => (
  <DataSection title="Additional Information">
    <DataRow label="Reason for Unallocated">
      <DataField value={reasonForUnallocated}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>

    <DataRow label="T.Bili - Result 1">
      <DataField value={liverData?.totalBilirubinRecentResult1}>{(v) => <Chip size="sm">{v} mg/dL</Chip>}</DataField>
    </DataRow>

    <DataRow label="T.Bili - Result 2">
      <DataField value={liverData?.totalBilirubinRecentResult2}>{(v) => <Chip size="sm">{v} mg/dL</Chip>}</DataField>
    </DataRow>

    <DataRow label="SGOT / AST - Result 1">
      <DataField value={liverData?.aspartateAminotransferaseRecentResult1}>
        {(v) => <Chip size="sm">{v} U/L</Chip>}
      </DataField>
    </DataRow>

    <DataRow label="SGOT / AST - Result 2">
      <DataField value={liverData?.aspartateAminotransferaseRecentResult2}>
        {(v) => <Chip size="sm">{v} U/L</Chip>}
      </DataField>
    </DataRow>

    <DataRow label="SGPT / ALT - Result 1">
      <DataField value={liverData?.alanineAminotransferaseRecentResult1}>
        {(v) => <Chip size="sm">{v} U/L</Chip>}
      </DataField>
    </DataRow>

    <DataRow label="SGPT / ALT - Result 2">
      <DataField value={liverData?.alanineAminotransferaseRecentResult2}>
        {(v) => <Chip size="sm">{v} U/L</Chip>}
      </DataField>
    </DataRow>

    <CtScanDataRows
      ctScan={liverData?.ctScan}
      ctScanDetails={liverData?.ctScanDetails}
      ctScanLink={liverData?.ctScanLink}
      ctScanDocuments={liverData?.ctScanDocuments}
    />

    <DataRow label="Local Surgeon to Procure?">
      <DataField value={liverData?.willLocalSurgeonPerformOrganProcurement}>
        {(v) => <Chip size="sm">{yesNo(v)}</Chip>}
      </DataField>
    </DataRow>

    {liverData?.willLocalSurgeonPerformOrganProcurement === true && (
      <DataRow label="Local Surgeon to Procure Details">
        <DataField value={liverData?.localSurgeonToProcureDetails}>{(v) => <DataText>{v}</DataText>}</DataField>
      </DataRow>
    )}

    <DataRow label="Sedation">
      <DataField value={liverData?.sedation}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>
  </DataSection>
);

const HeartAdditionalInfo = ({ heartData }: { heartData: DonorData['heartData'] }) => (
  <DataSection title="Additional Information">
    <DataRow label="List of OCS Centers on Match Run">
      <DataField value={heartData?.matchRunOcsCenterNames}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>
  </DataSection>
);

const LungsAdditionalInfo = ({ lungData }: { lungData: DonorData['lungData'] }) => (
  <DataSection title="Additional Information">
    <DataRow label="List of OCS Centers on Match Run">
      <DataField value={lungData?.matchRunOcsCenterNames}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>

    <DataRow label="Most Recent PF Ratio">
      <DataField value={lungData?.mostRecentPfRatio}>{(v) => <Chip size="sm">{v} mmHg</Chip>}</DataField>
    </DataRow>

    <DataRow label="24 hr Fluid Balance">
      <DataField value={lungData?.netFluidBalanceOverPast24Hours}>{(v) => <Chip size="sm">{v} mL</Chip>}</DataField>
    </DataRow>

    <DataRow label="Culture Sensitivities">
      <DataField value={lungData?.cultureSensitivityResults}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>

    <CtScanDataRows
      ctScan={lungData?.ctScan}
      ctScanDetails={lungData?.ctScanDetails}
      ctScanLink={lungData?.ctScanLink}
      ctScanDocuments={lungData?.ctScanDocuments}
    />

    <DataRow label="Sedation">
      <DataField value={lungData?.sedation}>{(v) => <DataText>{v}</DataText>}</DataField>
    </DataRow>
  </DataSection>
);

export const OpoDonorDataView = ({ matchId, donorData, organType, donorType }: OpoDonorDataViewProps) => {
  const demographics = donorData?.donorDemographics;
  const parameters = donorData?.donorParameters;
  const blood = donorData?.donorBlood;

  const showNeuroStatus = donorType === DonorType.DCD;

  const weight = demographics?.weight;
  const height = demographics?.height;
  const bmi = !isNil(weight) && !isNil(height) ? calcBmi(weight, height) : null;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <DataSection title="Case Match">
        <DataRow label="Match ID">
          <DataField value={matchId || null}>{(v) => <Chip size="sm">{v}</Chip>}</DataField>
        </DataRow>
      </DataSection>

      <DataSection title="Donor Demographics">
        <DataRow label="Age">
          <DataField value={demographics?.age}>{(v) => <Chip size="sm">{v} years</Chip>}</DataField>
        </DataRow>

        <DataRow label="Weight">
          <DataField value={weight}>
            {(v) => (
              <>
                <Chip size="sm">{gramsToKgFormatted(v)}</Chip>
                <Chip size="sm">{gramsToLbFormatted(v)}</Chip>
              </>
            )}
          </DataField>
        </DataRow>

        <DataRow label="Height">
          <DataField value={height}>
            {(v) => (
              <>
                <Chip size="sm">{mmToCmFormatted(v)}</Chip>
                <Chip size="sm">{mmToFtInFormatted(v)}</Chip>
              </>
            )}
          </DataField>
        </DataRow>

        <DataRow label="BMI">
          <DataField value={bmi}>{(v) => <Chip size="sm">{v}</Chip>}</DataField>
        </DataRow>

        <DataRow label="Gender">
          <DataField value={demographics?.gender}>{(v) => <Chip size="sm">{GENDER_LABELS[v]}</Chip>}</DataField>
        </DataRow>

        <DataRow label="COD">
          <DataField value={demographics?.causeOfDeath}>
            {(v) => <Chip size="sm">{CAUSE_OF_DEATH_LABELS[v]}</Chip>}
          </DataField>
        </DataRow>
      </DataSection>

      <DataSection title="Parameters">
        {showNeuroStatus && (
          <DataRow label="Neuro Status">
            <DataField value={parameters?.neuroStatus}>
              {(v) => <Chip size="sm">{NEURO_STATUS_LABELS[v]}</Chip>}
            </DataField>
          </DataRow>
        )}

        <DataRow label="Vasopressors">
          <DataField value={parameters?.vasopressors}>{(v) => <Chip size="sm">{yesNo(v)}</Chip>}</DataField>
        </DataRow>

        {parameters?.vasopressors === true && (
          <DataRow label="Vasopressors Details">
            <DataField value={parameters?.vasopressorsDetails}>{(v) => <DataText>{v}</DataText>}</DataField>
          </DataRow>
        )}

        <DataRow label="Sustained Hypotension">
          <DataField value={parameters?.sustainedHypotension}>{(v) => <Chip size="sm">{yesNo(v)}</Chip>}</DataField>
        </DataRow>

        {parameters?.sustainedHypotension === true && (
          <DataRow label="Sustained Hypotension Details">
            <DataField value={parameters?.sustainedHypotensionDetails}>{(v) => <DataText>{v}</DataText>}</DataField>
          </DataRow>
        )}
      </DataSection>

      <DataSection title="Blood">
        <DataRow label="ABO">
          <DataField value={blood?.abo}>{(v) => <Chip size="sm">{v}</Chip>}</DataField>
        </DataRow>

        <DataRow label="Positive Serologies">
          <DataField value={blood?.positiveSerologies}>{(v) => <DataText>{v}</DataText>}</DataField>
        </DataRow>
      </DataSection>

      {organType === OrganType.Liver && (
        <LiverAdditionalInfo
          liverData={donorData?.liverData ?? null}
          reasonForUnallocated={donorData?.reasonForUnallocated}
        />
      )}

      {organType === OrganType.Heart && (
        <>
          <DataSection title="Additional Information">
            <DataRow label="Reason for Unallocated">
              <DataField value={donorData?.reasonForUnallocated}>{(v) => <DataText>{v}</DataText>}</DataField>
            </DataRow>
          </DataSection>

          <HeartAdditionalInfo heartData={donorData?.heartData ?? null} />
        </>
      )}

      {organType === OrganType.Lungs && (
        <>
          <DataSection title="Additional Information">
            <DataRow label="Reason for Unallocated">
              <DataField value={donorData?.reasonForUnallocated}>{(v) => <DataText>{v}</DataText>}</DataField>
            </DataRow>
          </DataSection>

          <LungsAdditionalInfo lungData={donorData?.lungData ?? null} />
        </>
      )}
    </div>
  );
};
